import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: 'login' | 'register';
}

type Mode = 'login' | 'register' | 'forgot';

const ERROR_MESSAGES: Record<string, string> = {
  'Invalid login credentials': 'E-posta veya şifre hatalı.',
  'Email not confirmed': 'E-posta adresinizi doğrulamanız gerekiyor.',
  'User already registered': 'Bu e-posta adresi zaten kayıtlı.',
  'Password should be at least 6 characters': 'Şifre en az 6 karakter olmalıdır.',
  'BU_SITEDE_HESAP_YOK': 'Bu sitede hesabınız bulunmuyor. Lütfen üye olun.',
};

function translateError(msg: string): string {
  for (const [key, val] of Object.entries(ERROR_MESSAGES)) {
    if (msg.includes(key)) return val;
  }
  return msg;
}

export default function LoginModal({ isOpen, onClose, initialMode = 'login' }: LoginModalProps) {
  const { signIn, signUp, resetPassword } = useAuth();
  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setEmail(''); setPassword(''); setName(''); setError(''); setSuccess('');
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen, initialMode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (mode === 'login') {
      const { error: err } = await signIn(email, password);
      if (err) {
        setError(translateError(err));
      } else {
        onClose();
      }
    } else if (mode === 'register') {
      if (!name.trim()) { setError('Ad Soyad alanı zorunludur.'); setLoading(false); return; }
      if (password.length < 6) { setError('Şifre en az 6 karakter olmalıdır.'); setLoading(false); return; }
      const { error: err } = await signUp(email, password, name);
      if (err) {
        setError(translateError(err));
      } else {
        setSuccess('Hesabınız oluşturuldu! Giriş yapabilirsiniz.');
        setTimeout(() => { setMode('login'); setSuccess(''); }, 2000);
      }
    } else {
      const { error: err } = await resetPassword(email);
      if (err) {
        setError(translateError(err));
      } else {
        setSuccess('Şifre sıfırlama bağlantısı e-posta adresinize gönderildi.');
      }
    }

    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-md bg-white rounded-2xl overflow-hidden">
        <div className="px-8 pt-8 pb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-serif text-2xl font-bold text-coffee">
              {mode === 'login' ? 'Giriş Yap' : mode === 'register' ? 'Üye Ol' : 'Şifremi Unuttum'}
            </h2>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-cream transition-colors text-coffee-light cursor-pointer"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <label className="block text-xs font-medium text-coffee-medium mb-1.5">Ad Soyad</label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Adınızı girin"
                  className="w-full px-4 py-3 text-sm bg-cream rounded-xl border border-transparent focus:border-gold focus:outline-none focus:bg-white transition-all text-coffee"
                  required
                />
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-coffee-medium mb-1.5">E-posta</label>
              <input
                type="email"
                name="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="ornek@mail.com"
                className="w-full px-4 py-3 text-sm bg-cream rounded-xl border border-transparent focus:border-gold focus:outline-none focus:bg-white transition-all text-coffee"
                required
              />
            </div>

            {mode !== 'forgot' && (
              <div>
                <label className="block text-xs font-medium text-coffee-medium mb-1.5">Şifre</label>
                <div className="relative">
                  <input
                    type={showPass ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="En az 6 karakter"
                    className="w-full px-4 py-3 text-sm bg-cream rounded-xl border border-transparent focus:border-gold focus:outline-none focus:bg-white transition-all text-coffee pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-coffee-light hover:text-coffee transition-colors cursor-pointer"
                  >
                    <i className={`text-sm ${showPass ? 'ri-eye-off-line' : 'ri-eye-line'}`} />
                  </button>
                </div>
              </div>
            )}

            {mode === 'login' && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => { setMode('forgot'); setError(''); setSuccess(''); }}
                  className="text-xs text-gold hover:underline transition-colors cursor-pointer"
                >
                  Şifremi Unuttum
                </button>
              </div>
            )}

            {error && (
              <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 px-3 py-2.5 rounded-xl">
                <i className="ri-error-warning-line mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div className="flex items-start gap-2 text-xs text-green-700 bg-green-50 px-3 py-2.5 rounded-xl">
                <i className="ri-checkbox-circle-line mt-0.5 flex-shrink-0" />
                <span>{success}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 bg-coffee hover:bg-coffee-medium text-white font-semibold rounded-xl transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin" />
                  İşleniyor...
                </span>
              ) : mode === 'login' ? 'Giriş Yap' : mode === 'register' ? 'Üye Ol' : 'Sıfırlama Linki Gönder'}
            </button>
          </form>
        </div>

        <div className="px-8 py-5 bg-cream border-t border-cream-dark text-center">
          {mode === 'login' ? (
            <p className="text-sm text-coffee-light">
              Hesabın yok mu?{' '}
              <button
                onClick={() => { setMode('register'); setError(''); setSuccess(''); }}
                className="text-gold font-medium hover:underline transition-colors cursor-pointer"
              >
                Üye Ol
              </button>
            </p>
          ) : mode === 'register' ? (
            <p className="text-sm text-coffee-light">
              Zaten hesabın var mı?{' '}
              <button
                onClick={() => { setMode('login'); setError(''); setSuccess(''); }}
                className="text-gold font-medium hover:underline transition-colors cursor-pointer"
              >
                Giriş Yap
              </button>
            </p>
          ) : (
            <p className="text-sm text-coffee-light">
              <button
                onClick={() => { setMode('login'); setError(''); setSuccess(''); }}
                className="text-gold font-medium hover:underline transition-colors cursor-pointer"
              >
                ← Giriş sayfasına dön
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
